import { useCallback, useReducer } from 'react';
import { logger } from '../../services/logger';
import { Item } from '../../domain/item';
import { useObjectUrl } from '../../hooks/useObjectUrl';
import { processImage } from '../../services/imageProcessing';
import { useFeedback } from '../feedback/FeedbackProvider';
import {
  imageWorkflowReducer,
  initialImageWorkflowState,
} from './imageWorkflowReducer';

export function useItemImageWorkflow({
  selectedItem,
  addOpen,
  detailsOpen,
  lastUsedLocation,
  setLocation,
  setQty,
  openAdd,
}: {
  selectedItem: Item | null;
  addOpen: boolean;
  detailsOpen: boolean;
  lastUsedLocation: string;
  setLocation: (value: string) => void;
  setQty: (value: number | string) => void;
  openAdd: () => void;
}) {
  const { notify } = useFeedback();
  const [state, dispatch] = useReducer(
    imageWorkflowReducer,
    initialImageWorkflowState,
  );
  const set = (
    field: keyof typeof state,
    value: (typeof state)[keyof typeof state],
  ) => dispatch({ type: 'set', field, value });
  const previewUrl = useObjectUrl(state.processedBlob);
  const image = previewUrl || state.externalUrl;
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);
  const clearPendingImage = () => {
    set('externalUrl', null);
    set('processedBlob', null);
    set('removeExistingImage', false);
  };

  const handleImageCapture = async (file: File) => {
    try {
      set('processing', true);
      set('progress', 0);
      const processed = await processImage(file, {
        onProgress: (value) => set('progress', Math.round(value)),
      });
      if (detailsOpen && selectedItem) {
        set('externalUrl', null);
        set('processedBlob', processed);
        set('removeExistingImage', false);
        return;
      }
      if (!addOpen && !detailsOpen) {
        setQty(1);
        setLocation(lastUsedLocation);
      }
      set('externalUrl', null);
      set('processedBlob', processed);
      set('removeExistingImage', false);
      openAdd();
    } catch (error) {
      logger.error('Image processing failed.', error);
      notify('Failed to process the image. Please try again.', 'error');
    } finally {
      set('processing', false);
      set('progress', 0);
    }
  };

  const handleClearImage = async (): Promise<boolean> => {
    if (!selectedItem?.image && !state.processedBlob) return false;
    set('externalUrl', null);
    set('processedBlob', null);
    set('removeExistingImage', true);
    return true;
  };

  return {
    image,
    croppedImageBlob: state.processedBlob,
    hasPendingImage: Boolean(state.processedBlob || state.externalUrl),
    removeExistingImage: state.removeExistingImage,
    compressionInProgress: state.processing,
    compressionProgress: state.progress,
    setExternalImageUrl: (value: string | null) => set('externalUrl', value),
    setCroppedImageBlob: (value: Blob | null) => set('processedBlob', value),
    reset,
    clearPendingImage,
    handleImageCapture,
    handleClearImage,
  };
}
